import { arrow, autoUpdate, flip, hide, offset, type Placement, useFloating } from '@floating-ui/react';
import { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';

import type { Color } from '../../../types/colors.ts';
import { SgTooltipContent } from './SgTooltipContent.tsx';
import type { SgTooltipMode } from './SgTooltipContext';
import { SgTooltipContext } from './SgTooltipContext';
import { SgTooltipTrigger } from './SgTooltipTrigger.tsx';

type SgTooltipProps = {
  placement?: Placement;
  color?: Color;
  backgroundColor?: Color;
  mode: SgTooltipMode;
  arrow?: boolean;
  defaultOpen?: boolean;
};

export const SgTooltip = (props: PropsWithChildren<SgTooltipProps>) => {
  const {
    placement = 'top',
    color = 'white',
    backgroundColor = 'primary',
    mode = 'hover',
    arrow: isArrowVisible = true,
    defaultOpen,
    children,
  } = props;

  const [isOpen, setIsOpen] = useState(defaultOpen ?? mode === 'persistent');
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  const arrowRef = useRef<SVGSVGElement | null>(null);

  const {
    refs,
    floatingStyles,
    middlewareData,
    context: floatingContext,
    update,
  } = useFloating({
    placement,
    open: isOpen,
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      ...(isArrowVisible
        ? [
            flip({
              crossAxis: false,
            }),
            offset(10),
            arrow({
              element: arrowRef,
            }),
          ]
        : []),
      hide(),
    ],
  });

  // Add this useEffect to update position of arrow because its size depends on content
  // and at the first render the size is not yet known, and the arrow position is wrong.
  useEffect(() => {
    if (isOpen && update) {
      const timer = setTimeout(() => {
        update();
      }, 0);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isOpen, update]);

  const contextValue = useMemo(
    () => ({
      placement,
      color,
      backgroundColor,
      refs,
      floatingStyles,
      isOpen,
      setIsOpen,
      mode,
      referenceElement,
      setReferenceElement,
      floatingContext,
      arrow: middlewareData.arrow,
      hide: middlewareData.hide,
      arrowRef,
    }),
    [
      placement,
      color,
      backgroundColor,
      refs,
      floatingStyles,
      isOpen,
      mode,
      referenceElement,
      floatingContext,
      middlewareData.arrow,
      middlewareData.hide,
    ],
  );

  return <SgTooltipContext.Provider value={contextValue}>{children}</SgTooltipContext.Provider>;
};

SgTooltip.displayName = 'SgTooltip';
SgTooltip.Trigger = SgTooltipTrigger;
SgTooltip.Content = SgTooltipContent;
